import React, { useState } from 'react'
import {
  AlertTypeEnums,
  ContactTypeEnums,
  AlertSettingsItem,
  REGEX_EMAIL_VALIDATION,
  REGEX_PHONE_VALIDATION,
} from '../../common/constants'

interface NotificationRowProps {
  setting: AlertSettingsItem | undefined
  contactType: ContactTypeEnums
  itemOrder: 1 | 2 | 3
  alertType: AlertTypeEnums
  onSettingChange: (alertType: AlertTypeEnums, contactType: ContactTypeEnums, itemOrder: number, setting: Partial<AlertSettingsItem>) => void
  disabled?: boolean
}

export function NotificationRow({
  setting,
  contactType,
  itemOrder,
  alertType,
  onSettingChange,
  disabled,
}: NotificationRowProps) {
  // Local state for form fields (controlled by parent)
  const enabled = setting?.enabled ?? false
  const contact = setting?.contact ?? ''
  const message = setting?.message ?? ''
  const totalAttempts = setting?.totalAttempts ?? 1
  const retryDelay = setting?.retryDelay ?? 3

  const [contactError, setContactError] = useState<string>('')

  // Validate contact based on type
  const validateContact = (value: string): boolean => {
    // Allow empty contact
    if (!value.trim()) {
      setContactError('')
      return true
    }

    const isValid =
      contactType === ContactTypeEnums.EMAIL
        ? REGEX_EMAIL_VALIDATION.test(value)
        : REGEX_PHONE_VALIDATION.test(value)

    if (!isValid) {
      setContactError(
        contactType === ContactTypeEnums.EMAIL
          ? 'Invalid email format'
          : 'Invalid phone format (use E.164: +1234567890)'
      )
      return false
    }

    setContactError('')
    return true
  }

  // Handle enabled checkbox change
  const handleEnabledChange = (checked: boolean) => {
    onSettingChange(alertType, contactType, itemOrder, { enabled: checked })
  }

  // Handle contact input change
  const handleContactChange = (value: string) => {
    if (contactError) {
      setContactError('')
    }
    onSettingChange(alertType, contactType, itemOrder, { contact: value })
  }

  // Handle contact blur (validate)
  const handleContactBlur = () => {
    validateContact(contact)
  }

  // Handle message change
  const handleMessageChange = (value: string) => {
    onSettingChange(alertType, contactType, itemOrder, { message: value })
  }

  // Handle total attempts change
  const handleTotalAttemptsChange = (value: number) => {
    onSettingChange(alertType, contactType, itemOrder, { totalAttempts: value })
  }

  // Handle retry delay change
  const handleRetryDelayChange = (value: number) => {
    onSettingChange(alertType, contactType, itemOrder, { retryDelay: value })
  }

  // Label for the row
  const label = `${contactType === ContactTypeEnums.EMAIL ? 'Email' : 'SMS'} ${itemOrder}`

  return (
    <tr className="hover:bg-gray-50">
      {/* Enabled checkbox */}
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => handleEnabledChange(e.target.checked)}
          disabled={disabled}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </td>

      {/* Item label */}
      <td className="px-4 py-3 text-sm font-medium text-gray-900">{label}</td>

      {/* Contact input */}
      <td className="px-4 py-3">
        <div>
          <input
            type="text"
            value={contact}
            onChange={(e) => handleContactChange(e.target.value)}
            onBlur={handleContactBlur}
            disabled={disabled}
            placeholder={contactType === ContactTypeEnums.EMAIL ? 'email@example.com' : '+1234567890'}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
              contactError ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {contactError && <p className="text-xs text-red-600 mt-1">{contactError}</p>}
        </div>
      </td>

      {/* Custom message input */}
      <td className="px-4 py-3">
        <input
          type="text"
          value={message}
          onChange={(e) => handleMessageChange(e.target.value)}
          disabled={disabled}
          placeholder="Optional custom message"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </td>

      {/* Total attempts input */}
      <td className="px-4 py-3">
        <input
          type="number"
          min="0"
          value={totalAttempts}
          onChange={(e) => handleTotalAttemptsChange(Math.max(0, parseInt(e.target.value) || 0))}
          disabled={disabled}
          className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </td>

      {/* Retry delay input */}
      <td className="px-4 py-3">
        <input
          type="number"
          min="0"
          value={retryDelay}
          onChange={(e) => handleRetryDelayChange(Math.max(0, parseInt(e.target.value) || 0))}
          disabled={disabled}
          className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </td>
    </tr>
  )
}
